/**
 * voice-v8 — Die Vorlese-Quelle: EIN Stand für Vorlese-Karte und Mini-Player in der Fußzeile (19.10.2026).
 *
 * Martin: Läuft eine Vorlesung, soll sie unten in der Fußzeile als kleiner Player auf jeder Seite stehen — mit
 * derselben Wiedergabe wie die Karte an der Antwort, nicht als zweite Audio-Instanz.
 *
 * Darum gibt es hier KEINE Wiedergabe und keinen eigenen Zustand der Wiedergabe. Eine Quelle gehört zu EINEM
 * Vorlese-Knopf der Blase (`.bubble-play`) und liest von ihm:
 *   - den Zustand (`data-state`: idle, loading, playing, paused, wartet) — der Knopf bleibt die Wahrheit;
 *   - die Stelle (Ereignis `voice-v8:vorlese-stand` am Knopf, `voice-v8:vorlese-gemerkt` am Fenster).
 * Bedient wird nur über den Knopf selbst: Klick = Laden/Pause/Weiter (wie bisher), `voice-v8:vorlese-ab` = ab einer
 * Stelle, `voice-v8:vorlese-stopp` = ganz beenden. Karte und Mini-Player rufen dieselben Methoden — es gibt keinen
 * Weg, auf dem die eine etwas anderes zeigt als die andere.
 *
 * Aktiv ist die Quelle, deren Knopf lädt, spricht, pausiert oder auf eine Berührung wartet. `beobachteVorlesen`
 * meldet die aktive (oder null) — daraus zeichnet die Abspielleiste (ton-leiste.ts) ihren Vorlese-Zustand.
 *
 * ⚠️ Das Vorlesen im Chat ist eine Streaming-Vertonung je Stück: keine Gesamtdauer, keine Sekunden. Der Schieber zählt
 *    ABSCHNITTE des Textes (vorlese-fortsetzen.ts `abschnittsStarts`), nicht Sekunden.
 * ⚠️ Das Mikro berührt diese Datei nie: Weder Klick noch Stopp öffnen oder sperren es (das tun nur Pille und
 *    Sprechweg in app.ts wie bisher).
 */
import { EREIGNIS_GEMERKT, abschnittVon, abschnittsStarts, fortsetzKennung, ladeVersatz, loescheVersatz } from "./vorlese-fortsetzen.js";

export const EREIGNIS_STAND = "voice-v8:vorlese-stand";
export const EREIGNIS_AB = "voice-v8:vorlese-ab";
export const EREIGNIS_STOPP = "voice-v8:vorlese-stopp";

/** Schieber-Sprünge werden gebündelt: Pfeiltasten und Tipps auf „Abschnitt vor“ lösen je ein Ereignis aus, jeder Sprung wäre ein neuer Strom (A2). */
export const SPRUNG_PAUSE_MS = 400;

/** Zustände des Knopfs, in denen „etwas läuft oder pausiert ist“. */
const LAEUFT = new Set(["loading", "playing", "paused", "wartet"]);

/** Wie viele Zeichen des Textes der Titel der Leiste zeigt. */
const TITEL_ZEICHEN = 36;

/** Kurzer Titel für die Leiste: Name der Quelle und die ersten Worte des Textes („Antwort · „Der erste Satz …“"). */
export function vorleseTitel(sprech: string, name = "Antwort"): string {
  const t = sprech.replace(/\s+/g, " ").trim();
  if (!t) return name;
  if (t.length <= TITEL_ZEICHEN) return `${name} · „${t}“`;
  const kurz = t.slice(0, TITEL_ZEICHEN);
  const sp = kurz.lastIndexOf(" ");
  return `${name} · „${(sp > 12 ? kurz.slice(0, sp) : kurz).trimEnd()} …“`;
}

/** „Teil 2 von 7“ — derselbe Wortlaut in Vorlese-Karte, Schieber-Ansage und Mini-Player (21.10.2026, vorher „Abschnitt“). */
export function teilText(index: number, anzahl: number): string {
  return `Teil ${index + 1} von ${anzahl}`;
}

export interface VorleseQuelle {
  readonly knopf: HTMLButtonElement;
  /** Der Sprechtext (Maßstab der Versätze). */
  sprech(): string;
  /** Anfänge der Abschnitte; nur ein Eintrag (0) heißt: kein Schieber. */
  starts(): number[];
  titel(): string;
  /** Zustand des Knopfs (`idle` …). */
  zustand(): string;
  /** Läuft, lädt, pausiert oder wartet die Wiedergabe? */
  laeuft(): boolean;
  /** Stelle im Sprechtext (Zeichen); 0 = Anfang / zu Ende gehört. */
  versatz(): number;
  /**
   * Gehörte Zeit seit dem Start in Sekunden (nur die Zeit im Zustand `playing`; Laden und Pause zählen nicht, Sprünge
   * setzen sie nicht zurück). KEINE Position im Ton und keine Gesamtdauer — die kennt das Streaming nicht.
   */
  laufzeitSek(): number;
  /** Abschnitt (Index in `starts()`), in dem die Stelle liegt — ein ausstehender Sprung zählt schon. */
  abschnitt(): number;
  /** Bei jeder Änderung von Zustand oder Stelle rufen. Gibt die Abmeldung zurück. */
  abonniere(fn: () => void): () => void;
  /** Laden/Pause/Weiter — der Klick auf den Vorlese-Knopf. */
  umschalten(): void;
  /** Ab diesem Abschnitt lesen — gebündelt (`SPRUNG_PAUSE_MS`). */
  springeZuAbschnitt(index: number): void;
  /** Einen Abschnitt vor (+1) oder zurück (−1). */
  springeUm(schritt: 1 | -1): void;
  /** Von vorn: Stelle vergessen und ab 0 lesen. */
  vonVorn(): void;
  /** Ganz beenden (die Stelle bleibt gemerkt). */
  stoppe(): void;
  /** In den Chat zur Antwort springen. false: die Blase gibt es nicht mehr. */
  zurKarte(): boolean;
}

type TextQuelle = string | (() => string);

class Quelle implements VorleseQuelle {
  private text: string | null = null;
  private startsCache: number[] | null = null;
  private versatzWert: number | null = null;
  /** Letzte echt gemeldete Stelle — dorthin geht die Anzeige zurück, wenn ein Sprung abgebrochen wird. */
  private standWert: number | null = null;
  private kennung: string | null = null;
  private readonly hoerer = new Set<() => void>();
  private sprungTimer: ReturnType<typeof setTimeout> | null = null;
  private lauscht = false;
  /** Laufzeit: Summe der abgeschlossenen Spiel-Abschnitte (ms) und Beginn des laufenden (null: spielt gerade nicht). */
  private laufSummeMs = 0;
  private laufBeginn: number | null = null;

  constructor(
    readonly knopf: HTMLButtonElement,
    private readonly holeText: TextQuelle,
    private readonly name: string,
  ) {
    knopf.addEventListener(EREIGNIS_STAND, (ev) => {
      // Ein Sprung ist unterwegs: der alte Strom meldet noch seine alte Stelle, die darf ihn nicht überschreiben.
      if (this.sprungTimer !== null) return;
      const v = (ev as CustomEvent<{ versatz?: number }>).detail?.versatz;
      this.versatzWert = typeof v === "number" && v > 0 ? v : 0;
      this.standWert = this.versatzWert;
      this.melde();
    });
    try {
      new MutationObserver(() => this.melde()).observe(knopf, {
        attributes: true,
        attributeFilter: ["data-state", "data-mitlauf", "data-mitlauf-bis"],
      });
    } catch {
      /* ohne MutationObserver bleibt der Stand-Ereignis-Weg */
    }
  }

  sprech(): string {
    this.text ??= typeof this.holeText === "function" ? this.holeText() : this.holeText;
    return this.text;
  }
  starts(): number[] {
    return (this.startsCache ??= abschnittsStarts(this.sprech()));
  }
  titel(): string {
    return vorleseTitel(this.sprech(), this.name);
  }
  /**
   * 21.10.2026 (vorlese-mitlauf.ts): ruht der Knopf, spricht aber die Sprechtasten-Antwort genau diesen Text
   * (ohne Knopf), zeigt die Quelle deren Zustand — sonst stand die Karte auf „Vorlesen“, während es klang.
   */
  private mitlauf(): "playing" | "paused" | null {
    const m = this.knopf.dataset.mitlauf;
    return m === "playing" || m === "paused" ? m : null;
  }
  zustand(): string {
    const eigener = this.knopf.dataset.state ?? "idle";
    return eigener === "idle" ? (this.mitlauf() ?? eigener) : eigener;
  }
  laeuft(): boolean {
    return LAEUFT.has(this.zustand());
  }
  versatz(): number {
    this.hoereAmFenster();
    // Mitlauf: die Stelle der fremden Schlange zeigen, nicht merken (der gemerkte Versatz bleibt unberührt).
    if (this.sprungTimer === null && this.mitlauf() !== null && (this.knopf.dataset.state ?? "idle") === "idle") {
      const bis = Number(this.knopf.dataset.mitlaufBis);
      return Number.isFinite(bis) && bis > 0 ? Math.min(bis, this.sprech().length) : 0;
    }
    this.versatzWert ??= ladeVersatz(this.sprech()) ?? 0;
    return this.versatzWert;
  }
  laufzeitSek(): number {
    this.zaehleLaufzeit();
    return Math.floor((this.laufSummeMs + (this.laufBeginn === null ? 0 : Date.now() - this.laufBeginn)) / 1000);
  }
  /** Liest den Zustand des Knopfs: spielt er, läuft die Uhr; ruht er ganz (idle), beginnt die nächste Vorlesung bei 0. */
  private zaehleLaufzeit(): void {
    const jetzt = Date.now();
    if (this.zustand() === "playing") this.laufBeginn ??= jetzt;
    else if (this.laufBeginn !== null) {
      this.laufSummeMs += jetzt - this.laufBeginn;
      this.laufBeginn = null;
    }
    if (!this.laeuft()) this.laufSummeMs = 0;
  }
  abschnitt(): number {
    return abschnittVon(this.starts(), this.versatz());
  }
  abonniere(fn: () => void): () => void {
    this.hoereAmFenster();
    this.hoerer.add(fn);
    return () => {
      this.hoerer.delete(fn);
    };
  }

  /** Ein noch nicht abgeschickter Sprung verfällt (Pause, Stopp, Von vorn): er darf nicht nach 400 ms doch noch starten. */
  private bricheSprungAb(): void {
    if (this.sprungTimer === null) return;
    clearTimeout(this.sprungTimer);
    this.sprungTimer = null;
    this.versatzWert = this.standWert;
  }

  umschalten(): void {
    this.bricheSprungAb();
    this.knopf.click();
    this.melde();
  }

  springeZuAbschnitt(index: number): void {
    const starts = this.starts();
    const i = Math.max(0, Math.min(starts.length - 1, Math.round(index)));
    const ab = starts[i] ?? 0;
    this.versatzWert = ab;
    if (this.sprungTimer !== null) clearTimeout(this.sprungTimer);
    // Erst nach kurzer Ruhe springen: mehrere Schritte hintereinander ergeben EINEN Strom.
    this.sprungTimer = setTimeout(() => {
      this.sprungTimer = null;
      this.knopf.dispatchEvent(new CustomEvent(EREIGNIS_AB, { detail: { versatz: ab } }));
    }, SPRUNG_PAUSE_MS);
    this.melde();
  }

  springeUm(schritt: 1 | -1): void {
    this.springeZuAbschnitt(this.abschnitt() + schritt);
  }

  vonVorn(): void {
    this.bricheSprungAb();
    loescheVersatz(this.sprech());
    this.versatzWert = 0;
    this.knopf.dispatchEvent(new CustomEvent(EREIGNIS_AB, { detail: { versatz: 0 } }));
    this.melde();
  }

  stoppe(): void {
    this.bricheSprungAb();
    this.knopf.dispatchEvent(new CustomEvent(EREIGNIS_STOPP));
    this.zaehleLaufzeit(); // der Knopf ist jetzt ruhig: die nächste Vorlesung zählt wieder ab 0
  }

  zurKarte(): boolean {
    if (!this.knopf.isConnected) return false;
    const ziel = this.knopf.closest<HTMLElement>(".bubble") ?? this.knopf;
    if (document.body.dataset.activeView !== "chat") document.dispatchEvent(new CustomEvent("vespera:go-chat"));
    const ruhig = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;
    const los = (): void => ziel.scrollIntoView?.({ block: "center", behavior: ruhig ? "auto" : "smooth" });
    // Nach dem Seitenwechsel steht der Chat erst im nächsten Bild wieder im Layout.
    if (typeof requestAnimationFrame === "function") requestAnimationFrame(los);
    else los();
    return true;
  }

  /** Stelle von außen gemeldet (Fenster-Ereignis, z. B. automatisches Vorlesen ohne Knopf). */
  setzeVersatzVonAussen(v: number): void {
    if (this.sprungTimer !== null) return;
    this.versatzWert = v > 0 ? v : 0;
    this.standWert = this.versatzWert;
    this.melde();
  }
  kennungVon(): string {
    return (this.kennung ??= fortsetzKennung(this.sprech()));
  }

  private hoereAmFenster(): void {
    if (this.lauscht) return;
    this.lauscht = true;
    fensterHoerer.add(this);
    installiereFensterHoerer();
  }

  melde(): void {
    this.zaehleLaufzeit();
    for (const fn of Array.from(this.hoerer)) fn();
    aendereAktive(this);
  }
}

// --- Fenster-Ereignis „Stelle gemerkt“ (auch Wiedergaben ohne Knopf, z. B. automatisches Vorlesen) ---
const fensterHoerer = new Set<Quelle>();
let fensterAn = false;
function installiereFensterHoerer(): void {
  if (fensterAn || typeof window === "undefined") return;
  fensterAn = true;
  window.addEventListener(EREIGNIS_GEMERKT, (ev) => {
    const d = (ev as CustomEvent<{ id?: string; versatz?: number }>).detail;
    if (!d || typeof d.id !== "string") return;
    for (const q of Array.from(fensterHoerer)) {
      // Blase neu gezeichnet oder weg: Hörer entfernen (sonst wüchse die Menge mit jedem Chat).
      if (!q.knopf.isConnected) {
        fensterHoerer.delete(q);
        continue;
      }
      if (q.kennungVon() === d.id) q.setzeVersatzVonAussen(d.versatz ?? 0);
    }
  });
}

// --- Welche Quelle ist gerade aktiv? ---
const quellen = new WeakMap<HTMLButtonElement, Quelle>();
const beobachter = new Set<(q: VorleseQuelle | null) => void>();
let zuletzt: Quelle | null = null;

function aendereAktive(q: Quelle): void {
  if (q.laeuft()) zuletzt = q;
  else if (zuletzt === q) zuletzt = null;
  else return; // eine ruhende fremde Quelle ändert nichts an der Leiste
  for (const fn of Array.from(beobachter)) {
    try {
      fn(zuletzt);
    } catch {
      /* ein kaputter Beobachter darf die anderen nicht blockieren */
    }
  }
}

/**
 * Die Quelle zu einem Vorlese-Knopf — pro Knopf genau eine (Karte, Leiste und app.ts teilen sie).
 * `sprech` darf eine Funktion sein: der Sprechtext wird erst gebaut, wenn jemand ihn braucht.
 */
export function holeQuelle(knopf: HTMLButtonElement, sprech: TextQuelle, name = "Antwort"): VorleseQuelle {
  let q = quellen.get(knopf);
  if (!q) {
    q = new Quelle(knopf, sprech, name);
    quellen.set(knopf, q);
  }
  return q;
}

/** Die Quelle, die gerade läuft oder pausiert ist — oder null. */
export function aktiveVorleseQuelle(): VorleseQuelle | null {
  return zuletzt?.laeuft() ? zuletzt : null;
}

/** Bei jedem Wechsel der aktiven Quelle (und ihrer Stelle) benachrichtigt werden; ruft sofort einmal. */
export function beobachteVorlesen(fn: (q: VorleseQuelle | null) => void): () => void {
  beobachter.add(fn);
  try {
    fn(aktiveVorleseQuelle());
  } catch {
    /* siehe aendereAktive */
  }
  return () => {
    beobachter.delete(fn);
  };
}

/** Nur Tests: Merker der aktiven Quelle leeren. */
export function _leereVorleseQuellenFuerTests(): void {
  zuletzt = null;
  beobachter.clear();
}
